'use client';

/**
 * AccessRefusal - the one explanation of a refused or unknown access check (Q-2415 AC4/AC6).
 *
 * Every guard that withholds content (AuthGuard for whole pages; PermissionGuard, TierGuard and RoleGuard for
 * sections) says the same things in the same words: the signed-in account, the roles the app actually checked
 * (from the access-control context, never the JWT role hint), what is missing, the next step, and Retry and
 * Sign out. A failed access read is "Couldn't load your access", never a refusal. Everything renders as React
 * text only.
 */

import React from 'react';
import { Loader2 } from 'lucide-react';
import { SignOutForm } from './SignOutForm';
import { useOptionalAppShell } from '@/components/shell/AppShellContext';
import { isTransientAccessFailure } from '@/components/shell/accessReadRecovery';
import { collectHeldRoles, formatHeldRoles, ROLES_UNAVAILABLE_LABEL, type HeldRolesSource } from '@/lib/auth/held-roles';

/** The heading-level message for an access read that failed: access is unknown, not refused. */
export const ACCESS_UNKNOWN_MESSAGE = "Couldn't load your access. Check your connection and try again.";

/** One "label: value" line under the account lines (what the page or section needs, the current plan). */
export interface RefusalRow {
  label: string;
  value: string;
}

/** The held-role names the guards checked, or ROLES_UNAVAILABLE_LABEL when the access context is unknown. */
export function heldRolesLabel(source: HeldRolesSource | null | undefined): string {
  return source ? formatHeldRoles(collectHeldRoles(source)) : ROLES_UNAVAILABLE_LABEL;
}

/** The next-step sentence; `remedy` is what the administrator does, e.g. "give this account the required role". */
export function refusalNextStep(remedy: string): string {
  return `To get access, ask your agency administrator to ${remedy}, then select Retry. If you meant to use a different account, sign out and sign back in.`;
}

interface AccountExplanationProps {
  email: string | null | undefined;
  /** Held role names, or ROLES_UNAVAILABLE_LABEL when the access context is unknown. */
  roles: string;
  rows: RefusalRow[];
}

export function AccountExplanation({ email, roles, rows }: AccountExplanationProps) {
  return (
    <div className="text-center text-sm text-ink-secondary max-w-md space-y-1 break-words">
      <p>Signed in as: {email || 'Email unavailable'}</p>
      <p>Your role: {roles}</p>
      {rows.map((row) => (
        <p key={row.label}>
          {row.label}: {row.value}
        </p>
      ))}
    </div>
  );
}

export function RetryButton({ onRetry }: { onRetry: () => unknown }) {
  return (
    <button
      type="button"
      onClick={() => void onRetry()}
      className="px-4 py-2 text-sm text-ink bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors cursor-pointer"
    >
      Retry
    </button>
  );
}

export function SignOutButton() {
  return (
    <SignOutForm>
      <button
        type="submit"
        className="px-4 py-2 text-sm text-ink bg-raised hover:bg-floating rounded-lg transition-colors cursor-pointer"
      >
        Sign out
      </button>
    </SignOutForm>
  );
}

interface GuardRefusalProps {
  /** 'denied': the check ran and the account lacks what is needed. 'unknown': the access read failed. */
  state: 'denied' | 'unknown';
  /** Heading for a denial (e.g. "Permission Required"). An unknown state always uses ACCESS_UNKNOWN_MESSAGE. */
  title: string;
  /** Optional body under the denial heading. */
  message?: React.ReactNode;
  icon?: React.ReactNode;
  /** Held role names (heldRolesLabel). */
  roles: string;
  rows: RefusalRow[];
  /** What the administrator does, for refusalNextStep. */
  remedy: string;
  /** The access read error, when state is 'unknown'. */
  error?: unknown;
  /** Re-reads the access context. Defaults to the AppShell's refetch when inside AppShellProvider. */
  onRetry?: () => unknown;
  /** The checked account's email for reads outside AppShellProvider (e.g. the access context's user_email). */
  email?: string | null;
  /** Extra actions after Retry and Sign out (e.g. an upgrade link). */
  children?: React.ReactNode;
}

/** The in-content refusal used by PermissionGuard, TierGuard and RoleGuard. Content is never rendered here. */
export function GuardRefusal({ state, title, message, icon, roles, rows, remedy, error, onRetry, email: checkedEmail, children }: GuardRefusalProps) {
  const shell = useOptionalAppShell();
  const email = shell?.user?.email || checkedEmail;
  const retry = onRetry ?? shell?.refetchAccessControl;
  // AppShellProvider re-reads a transient failure on its own; outside it nothing retries automatically.
  const reconnecting = state === 'unknown' && !!shell && isTransientAccessFailure(error);

  return (
    <div
      role={state === 'unknown' ? 'alert' : undefined}
      className="flex flex-col items-center justify-center p-6 gap-4 bg-panel border border-edge rounded-lg"
    >
      {state === 'denied' && icon}
      <div className="text-center max-w-md">
        {state === 'denied' ? (
          <>
            <h3 className="text-sm font-medium text-ink mb-1">{title}</h3>
            {message && <div className="text-xs text-ink-muted">{message}</div>}
          </>
        ) : (
          <p className="text-sm text-ink">{ACCESS_UNKNOWN_MESSAGE}</p>
        )}
        {reconnecting && (
          <p role="status" className="mt-2 flex items-center justify-center gap-2 text-xs text-ink-muted">
            <Loader2 className="w-4 h-4 text-blue-500 animate-spin" aria-hidden="true" />
            Reconnecting automatically...
          </p>
        )}
      </div>
      <AccountExplanation email={email} roles={roles} rows={rows} />
      {state === 'denied' && <p className="text-center text-xs text-ink-muted max-w-md">{refusalNextStep(remedy)}</p>}
      <div className="flex flex-wrap items-center justify-center gap-3">
        {retry && <RetryButton onRetry={retry} />}
        <SignOutButton />
        {children}
      </div>
    </div>
  );
}
